import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { Box, Typography } from '@material-ui/core';

import BackButton from '../../../components/BackButton';
import ButtonRow from '../../../components/ButtonRow';
import InlineButton from '../../../components/InlineButton';
import InlineLink from '../../../components/InlineLink';
import Loader from '../../../components/Loader';
import Page from '../../../components/Page';
import BooleanOutput from '../../../components/form/BooleanOutput';
import DateOutput from '../../../components/form/DateOutput';
import TextOutput from '../../../components/form/TextOutput';
import FormSection from '../../../components/form/FormSection';
import {
  MinimalLessonLocation,
  Operator,
  SwimmingLessonProviderWithLessonLocations,
  User,
} from '../../../types';
import useUserTabs from '../hooks/user-tabs';
import UserRepository from '../repository/UserRepository';
import LinkToSLPDialog from '../../swimming-lesson-provider/components/LinkToSLPDialog';
import LinkToOperatorDialog from '../../swimming-lesson-provider/components/LinkToOperatorDialog';
import { ACCOUNT_BASE_ROUTE, USER_BASE_ROUTE } from '../index';
import Roles, { AdminRole, RoleInterface } from '../domain/Roles';
import SelectAdminRoleDialog from '../../swimming-lesson-provider/components/SelectAdminRoleDialog';

interface ViewUserProps {
  id?: string;
  isProfile: boolean;
}

const ViewUser = (props: ViewUserProps) => {
  const { id: passedId, isProfile } = props;
  const { id: paramId } = useParams<{ id?: string }>();
  const id = passedId || paramId || '';

  const account = useSelector(
    (selector: {
      user: {
        account: User;
      };
    }) => selector.user.account,
  );
  const isAdmin = account.roles.includes('ROLE_ADMIN');
  const isSuperAdmin = account.roles.includes('ROLE_SUPER_ADMIN');
  const isExaminer = account.roles.includes('ROLE_EXAMINER');

  const [user, setUser] = useState<User | null>(null);

  const adminRole: AdminRole | undefined = user?.explicitRoles?.includes(
    'ROLE_SUPER_ADMIN',
  )
    ? 'ROLE_SUPER_ADMIN'
    : user?.explicitRoles?.includes('ROLE_ADMIN')
    ? 'ROLE_ADMIN'
    : undefined;

  const baseRoute = isProfile ? ACCOUNT_BASE_ROUTE : `${USER_BASE_ROUTE}/${id}`;

  const tabs = useUserTabs(user, baseRoute);

  const roles = user?.explicitRoles
    ? user.explicitRoles
        .map((role: keyof RoleInterface) => Roles[role])
        .join(', ')
    : null;

  const isSLPUser = user?.explicitRoles?.includes(
    'ROLE_SWIMMING_LESSON_PROVIDER_USER',
  );
  const isSLPManager = user?.explicitRoles?.includes(
    'ROLE_SWIMMING_LESSON_PROVIDER_MANAGER',
  );

  const userRepository = new UserRepository();

  const loadUser = () => {
    userRepository.find(id).then((response) => setUser(response.data));
  };

  const demoteManagerToUser = () => {
    userRepository.demoteManagerToUser(id).then(loadUser);
  };

  const promoteUserToManager = () => {
    userRepository.promoteUserToManager(id).then(loadUser);
  };

  const [SLPDialogOpen, setSLPDialogOpen] = useState<boolean>(false);
  const [operatorDialogOpen, setOperatorDialogOpen] = useState<boolean>(false);
  const [adminRoleDialogOpen, setAdminRoleDialogOpen] =
    useState<boolean>(false);

  const openSLPDialog = () => {
    setSLPDialogOpen(true);
  };
  const openOperatorDialog = () => {
    setOperatorDialogOpen(true);
  };
  const openAdminRoleDialog = () => {
    setAdminRoleDialogOpen(true);
  };

  const closeSLPDialog = () => {
    setSLPDialogOpen(false);
  };
  const closeOperatorDialog = () => {
    setOperatorDialogOpen(false);
  };
  const closeAdminRoleDialog = () => {
    setAdminRoleDialogOpen(false);
  };

  const linkToSwimmingLessonProvider = (
    swimmingLessonProvider: SwimmingLessonProviderWithLessonLocations,
    lessonLocations: MinimalLessonLocation[],
    sendManual: boolean,
  ) => {
    userRepository
      .linkToSwimmingLessonProvider(
        id,
        swimmingLessonProvider.id,
        lessonLocations ? lessonLocations.map((ll) => ll.id) : [],
        sendManual,
      )
      .then(loadUser);

    closeSLPDialog();
  };

  const linkToOperator = (operator: Operator) => {
    userRepository.linkToOperator(id, operator.id).then(loadUser);

    closeOperatorDialog();
  };

  const updateAdminRole = (adminRole: AdminRole | undefined) => {
    userRepository.updateAdminRole(id, adminRole).then(loadUser);

    closeAdminRoleDialog();
  };

  const unlinkFromSwimmingLessonProvider = () => {
    userRepository.unlinkFromSwimmingLessonProvider(id).then(loadUser);
  };
  const unlinkFromOperator = () => {
    userRepository.unlinkFromOperator(id).then(loadUser);
  };

  useEffect(
    () => {
      loadUser();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const title = isProfile ? (
    'Profiel bekijken'
  ) : (
    <Box display="flex" alignItems="center" mb={3}>
      <Box mr={3}>
        <BackButton />
      </Box>
      <Typography variant="h1">Gebruiker bekijken</Typography>
    </Box>
  );

  if (!user) {
    return <Loader />;
  }

  const lessonLocationsAsString = user.grantedLessonLocations
    ? user.grantedLessonLocations.map((ll) => ll.name).join(', ')
    : '';

  return (
    <Page title={title}>
      <FormSection title="Gegevens">
        {(isAdmin || isExaminer) && (
          <TextOutput
            label={isExaminer ? 'Examinatorcode' : 'Afas ID'}
            value={user.afasId}
            hideIfEmpty
          />
        )}
        <TextOutput label="Naam" value={user.fullName} />
        <TextOutput label="E-mail" value={user.email} />
        <TextOutput
          label="Zwemlesaanbieder"
          value={user.swimmingLessonProvider?.name || ''}
          hideIfEmpty
        />
        <TextOutput
          label="Leslocaties"
          value={lessonLocationsAsString}
          hideIfEmpty
        />
        <TextOutput
          label="Exploitant"
          value={user.operator?.name || ''}
          hideIfEmpty
        />
        <TextOutput
          label="Telefoon (privé)"
          value={user.telephonePrivate}
          hideIfEmpty
        />
        <TextOutput
          label="Telefoon (mobiel)"
          value={user.telephonePrivateMobile}
          hideIfEmpty
        />
        <TextOutput
          label="Telefoon (werk)"
          value={user.telephoneWork}
          hideIfEmpty
        />
        <TextOutput label="Rollen" value={roles} hideIfEmpty />
      </FormSection>
      {user.delegateProfile && (
        <FormSection title="Gedelegeerdenprofiel">
          <TextOutput
            label="Status"
            value={user.delegateProfile.statusReadable}
          />
          <DateOutput label="Sinds" value={user.delegateProfile.startDate} />
          <BooleanOutput
            label="Licentie voor ABC"
            value={user.delegateProfile.licenseSwimAbc}
          />
          <BooleanOutput
            label="Licentie voor snorkelen"
            value={user.delegateProfile.licenseSnorkeling}
          />
          <BooleanOutput
            label="Licentie voor survival"
            value={user.delegateProfile.licenseSurvival}
          />
          <BooleanOutput
            label="Licentie voor zwemvaardigheid"
            value={user.delegateProfile.licenseAptitude}
          />
          <TextOutput
            label="IBAN"
            value={user.delegateProfile.iban}
            hideIfEmpty
          />
          <BooleanOutput
            label="Mag declareren"
            value={user.delegateProfile.mayInvoice}
          />
          <TextOutput
            label="Maximale afstand tot examens in km"
            value={user.delegateProfile.maxDistanceExamsInKm ?? 'N.B.'}
          />
          <BooleanOutput
            label="Inzetbaar"
            value={user.delegateProfile.available}
          />
        </FormSection>
      )}
      <ButtonRow>
        <InlineLink type="primary" to={`${baseRoute}/bewerken`}>
          Bewerken
        </InlineLink>
        <InlineLink type="primary" to={`${baseRoute}/wachtwoord`}>
          Wachtwoord veranderen
        </InlineLink>
        {isSuperAdmin && (
          <>
            {isSLPUser && (
              <InlineButton type="default" onClick={promoteUserToManager}>
                Geef managerrol
              </InlineButton>
            )}
            {isSLPManager && (
              <InlineButton type="default" onClick={demoteManagerToUser}>
                Neem managerrol af
              </InlineButton>
            )}
            {user.swimmingLessonProvider ? (
              <InlineButton
                type="default"
                onClick={unlinkFromSwimmingLessonProvider}
              >
                Loskoppelen van zwemlesaanbieder
              </InlineButton>
            ) : (
              <>
                <LinkToSLPDialog
                  isOpen={SLPDialogOpen}
                  onClose={closeSLPDialog}
                  onConfirm={linkToSwimmingLessonProvider}
                />
                <InlineButton type="default" onClick={openSLPDialog}>
                  Koppelen aan zwemlesaanbieder
                </InlineButton>
              </>
            )}
            {user.operator ? (
              <InlineButton type="default" onClick={unlinkFromOperator}>
                Loskoppelen van exploitant
              </InlineButton>
            ) : (
              <>
                <LinkToOperatorDialog
                  isOpen={operatorDialogOpen}
                  onClose={closeOperatorDialog}
                  onConfirm={linkToOperator}
                />
                <InlineButton type="default" onClick={openOperatorDialog}>
                  Koppelen aan exploitant
                </InlineButton>
              </>
            )}
            <SelectAdminRoleDialog
              adminRole={adminRole}
              isOpen={adminRoleDialogOpen}
              onClose={closeAdminRoleDialog}
              onConfirm={updateAdminRole}
            />
            <InlineButton type="default" onClick={openAdminRoleDialog}>
              Adminrol bewerken
            </InlineButton>
          </>
        )}
      </ButtonRow>
      {tabs}
    </Page>
  );
};

export default ViewUser;
